import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Calendar, List, User, LogOut, ChevronDown, Users, BarChart2, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useMsal } from "@azure/msal-react";
import { logoutFromMicrosoft } from '../lib/msGraph';
import { toast } from 'sonner';
import { supabase } from '../lib/supabase';
import MicrosoftLoginButton from './MicrosoftLoginButton';
import { createLog } from '../lib/logging';

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user, signOut, userRole } = useAuth();
  const { instance, accounts } = useMsal();
  const location = useLocation();

  const navigation = [
    { name: 'Termin planen', href: '/dashboard', icon: Calendar },
    { name: 'Terminübersicht', href: '/meetings', icon: List },
    { name: 'Analytics', href: '/analytics', icon: BarChart2 },
    ...(userRole === 'admin' ? [
      { name: 'Benutzerverwaltung', href: '/users', icon: Users },
      { name: 'System Logs', href: '/logs', icon: ClipboardList }
    ] : [])
  ];

  const microsoftAccount = accounts[0];

  const handleMicrosoftLogout = async () => {
    try {
      await logoutFromMicrosoft();

      // Remove Microsoft account from database
      if (user) {
        const { error } = await supabase
          .from('microsoft_accounts')
          .delete()
          .eq('user_id', user.id);

        if (error) {
          console.error('Error removing Microsoft account:', error);
          toast.error('Fehler beim Entfernen der Microsoft-Konto-Verbindung');
          return;
        }
      }

      await createLog({
        action: 'Microsoft Account Disconnected',
        entity_type: 'microsoft_account',
        entity_id: user?.id,
        level: 'info'
      });

      // Clear MSAL cache for this account
      if (microsoftAccount) {
        await instance.logoutRedirect({
          account: microsoftAccount,
          postLogoutRedirectUri: window.location.origin
        });
      }

      toast.success('Microsoft-Konto erfolgreich getrennt');
    } catch (error: any) {
      console.error("Error during Microsoft logout:", error);
      toast.error('Fehler beim Trennen des Microsoft-Kontos');
    }
  };

  const handleSignOut = async () => {
    try {
      // If Microsoft account is connected, disconnect it first
      if (microsoftAccount) {
        await handleMicrosoftLogout();
      }
      
      await createLog({
        action: 'User Logged Out',
        entity_type: 'user',
        entity_id: user?.id,
        level: 'info'
      });

      // Then sign out from Supabase
      await signOut();
    } catch (error) {
      console.error("Error during sign out:", error);
      toast.error('Fehler beim Abmelden');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow">
        <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <img
                  src="https://www.tok-rec2rec.com/wp-content/uploads/2023/12/Logo-rec2rec.svg"
                  alt="TOK rec2rec"
                  className="h-8"
                />
              </div>
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`${
                        location.pathname === item.href
                          ? 'border-green-500 text-gray-900'
                          : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                      } inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium`}
                    >
                      <Icon className="w-4 h-4 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center">
              <div className="relative group">
                <button className="flex items-center space-x-2 text-gray-700 hover:text-gray-900 focus:outline-none">
                  <User className="w-5 h-5" />
                  <span className="text-sm font-medium">{user?.email}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                <div className="absolute right-0 w-72 mt-2 py-2 bg-white rounded-lg shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {/* User Role Badge */}
                  {userRole && (
                    <div className="px-4 py-2">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        userRole === 'admin' 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {userRole === 'admin' ? 'Administrator' : 'Mitarbeiter'}
                      </span>
                    </div>
                  )}

                  <div className="border-t border-gray-100 my-2"></div>

                  {/* Microsoft Account Status */}
                  <div className="px-4 py-2">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Microsoft-Konto</p>
                      {microsoftAccount && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                          Verbunden
                        </span>
                      )}
                    </div>
                    {microsoftAccount ? (
                      <div className="space-y-2">
                        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded-md">
                          <p className="truncate text-xs">{microsoftAccount.username}</p>
                        </div>
                        <button
                          onClick={handleMicrosoftLogout}
                          className="w-full flex items-center justify-center px-3 py-1.5 text-sm border border-red-300 rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <LogOut className="w-4 h-4 mr-2" />
                          Microsoft-Konto trennen
                        </button>
                      </div>
                    ) : (
                      <div className="bg-gray-50 p-2 rounded-md">
                        <MicrosoftLoginButton />
                      </div>
                    )}
                  </div>
                  
                  <div className="border-t border-gray-100 my-2"></div>
                  
                  {/* Sign Out Button */}
                  <button
                    onClick={handleSignOut}
                    className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="w-4 h-4 mr-2" />
                    Abmelden
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-full mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}